import { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth, useUserData } from '../../lib/auth';
import { useDriverTour } from '../../lib/onboarding/useDriverTour';
import { useContextualTourTrigger } from '../../lib/onboarding/useContextualTourTrigger';
import { useMediaQuery } from '../../lib/hooks/useMediaQuery';
import { SetupFloatingBubble } from './SetupFloatingBubble';
import { OnboardingErrorBoundary } from './OnboardingErrorBoundary';
import { acquireFluTourOwnership } from '../../lib/onboarding/legacyTourOwnership';
import { GoalConsultant } from './GoalConsultant';
import { HabiConsultantPilot } from './HabiConsultantPilot';
import { habiOrganizationAllowed, pilotEnvironmentAllowed, pilotStorageKey } from '../../lib/onboarding/consultant/pilotGate';

/**
 * Prefissi delle route pubbliche / standalone (no Layout CRM). Su queste
 * pagine il setup floating NON deve mai comparire, anche se l'utente e loggato
 * in un'altra tab (es. dev che apre un link di prenotazione mentre e in sessione).
 */
const PUBLIC_ROUTE_PREFIXES = [
  '/login', '/logout', '/register', '/reset-password', '/2fa-setup',
  '/invite/', '/welcome/',
  '/auth/callback', '/newsletter/unsubscribe',
  '/sign/', '/signature/',
  '/book/', '/booking/manage/',
  '/s/', '/f/', '/v/', '/tracciabilita/', '/prodotto/',
  '/digital-google-connect', '/digital-microsoft-connect',
  '/meta-ads-callback', '/fatture-in-cloud',
  '/partner-portal', '/client-portal', '/collaborator-portal',
  '/404',
];

function isPublicRoute(pathname: string): boolean {
  return PUBLIC_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix),
  );
}

/**
 * Runtime onboarding globale — montato UNA volta in App.tsx dentro il Router
 * (e dentro AppProvider per accesso ai context auth).
 *
 * Centralizza:
 *  - useDriverTour: ascolta `?tour=` e lancia driver.js
 *  - useContextualTourTrigger: auto-trigger al primo ingresso in una sezione
 *  - SetupFloatingBubble: card fissa bottom-right per utenti non-admin
 *
 * NON gated sul trial: la checklist resta accessibile anche post-conversion
 * cosi l'utente puo finire i task setup anche dopo aver pagato. Se vuole
 * nasconderla, click su "Nascondi" → diventa bubble compatta in basso a dx.
 *
 * Perche qui e non in Layout: la route /settings NON wrappa in Layout per
 * evitare la doppia sidebar (CRM main + Settings tabs). Mettendo questi
 * effetti a livello router-root i tour funzionano ovunque, e il setup
 * floating resta visibile anche durante la configurazione delle impostazioni.
 */
export function OnboardingRuntime() {
  const { user, isSuperAdmin, isPartnerAdmin } = useAuth();
  const { organizationId } = useUserData();
  const location = useLocation();
  // Su mobile (< md) il bubble setup occupa troppo spazio e copre la lista:
  // nascondiamo solo la UI floating, gli hook dei tour restano attivi.
  const isMobile = useMediaQuery('(max-width: 767px)');

  const [pilotScope, setPilotScope] = useState<string | null>(null);
  const allowedPilot = pilotEnvironmentAllowed(import.meta.env.DEV || import.meta.env.VITE_HABI_PILOT_ENABLED === 'true',
    import.meta.env.VITE_SUPABASE_URL, import.meta.env.VITE_CORE_URL, window.location.hostname) && habiOrganizationAllowed(organizationId);
  useEffect(() => {
    if (!allowedPilot || !user?.id || !organizationId) { setPilotScope(null); return; }
    const key = pilotStorageKey(user.id, organizationId);
    const request = new URLSearchParams(location.search).get('consultant');
    try {
      if (request === 'habi' || request === 'goals') sessionStorage.setItem(key, request);
      if (request === 'off') sessionStorage.removeItem(key);
      setPilotScope(request === 'off' ? null : key);
    } catch { setPilotScope(request === 'off' ? null : key); }
  }, [allowedPilot, user?.id, organizationId, location.search]);

  const ownsOnboarding=!!user?.id && allowedPilot && !isPublicRoute(location.pathname) && !location.pathname.startsWith('/fattureincloud') && new URLSearchParams(location.search).get('consultant')!=='off';
  useLayoutEffect(()=>ownsOnboarding?acquireFluTourOwnership():undefined,[ownsOnboarding]);

  // I due hooks devono essere chiamati SEMPRE (Rules of Hooks). Il loro
  // contenuto interno e gia gated da `searchParams.get('tour')` e simili.
  useDriverTour(!ownsOnboarding);
  useContextualTourTrigger(!ownsOnboarding);

  if (!user) return null;
  // Pagine pubbliche/standalone (booking, firma, form, portal...): niente setup.
  if (isPublicRoute(location.pathname)) return null;
  // Griglia previsionale/fatturazione: la bolla copre il footer dei totali → nascondila.
  if (location.pathname.startsWith('/fattureincloud')) return null;
  // Explicit test-only opt-in; the repository itself rejects every non-DEV backend.
  // No effect on ordinary onboarding users.
  if (allowedPilot && organizationId && pilotScope === pilotStorageKey(user.id, organizationId)) {
    return <OnboardingErrorBoundary>{new URLSearchParams(location.search).get('consultant') === 'habi' ? <HabiConsultantPilot key={user.id} /> : <GoalConsultant key={organizationId+':'+user.id} />}</OnboardingErrorBoundary>;
  }
  if (isSuperAdmin || isPartnerAdmin) return null;
  if (isMobile) return null;
  return (
    <OnboardingErrorBoundary>
      <SetupFloatingBubble />
    </OnboardingErrorBoundary>
  );
}
